import { useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Input } from "antd5";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { LeadSortBy, LeadSortOrder } from "lib/types/graphQLEnums";
import { useDebouncedValue } from "../../../lib/debounce";
import {
  AddLeadEntitiesInput_Entities,
  SearchLeadsRequest,
} from "../../../lib/generated/app-service-gql/graphql";
import { useAddLeadEntities } from "../../../lib/hooks/api/leads/useAddLeadEntities";
import {
  DocumentFindLeadsMetadata,
  useCreateLeadOneClick,
} from "../../../lib/hooks/api/leads/useCreateLeadOneClick";
import { useInfiniteLeads } from "../../../lib/hooks/api/leads/useLeads";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { EventData } from "../../../lib/tracking";
import { Flex, Text } from "../../../styles/utility-components";
import { AllEntities, isBuyerOrganisation } from "../../entities/utils";
import { CreateLeadForm } from "../create_lead/CreateLeadForm";
import { CreateLeadFormOutput } from "../create_lead/utils";
import {
  bulkSaveEntitiesToAttach,
  BulkSaveEntity,
  entitiesToAttachAfterCreate,
} from "./bulkSaveEntityConfig";
import { LEAD_LIST_CONTAINER_ID, SaveToLeadList } from "./SaveToLeadList";

export const MAX_MATCHING_BUYERS = 30;

type Props = {
  /** Single-type selection. Provide this OR `entities` (mixed). */
  entityType?: AllEntities;
  entityIds?: string[];
  /** Mixed-source selection (e.g. `contact` + `contact_cognism`); takes precedence over the pair. */
  entities?: BulkSaveEntity[];
  defaultValues: Partial<CreateLeadFormOutput>;
  trackingData: EventData;
  isCreateFormOpen: boolean;
  onToggleCreateForm: (state: boolean) => void;
  savedLeadIds: string[];
  buyerMatchOnly?: boolean;
  findLeadsMetadata?: DocumentFindLeadsMetadata;
  onLeadSaved?: (leadId: string) => void;
};

// Cognism contacts are redeemed-on-save, so they're attached after the lead is created.
// Resolves that attach-list from whichever selection API the caller used.
function resolveEntitiesToAttach(
  entities: BulkSaveEntity[] | undefined,
  entityType: AllEntities | undefined,
  entityIds: string[] | undefined,
): AddLeadEntitiesInput_Entities[] | null {
  if (entities) return bulkSaveEntitiesToAttach(entities);
  if (entityType) return entitiesToAttachAfterCreate(entityType, entityIds ?? []);
  return null;
}

export function SaveToLead({
  defaultValues,
  entityIds,
  entityType,
  entities,
  isCreateFormOpen,
  onToggleCreateForm,
  savedLeadIds,
  trackingData,
  buyerMatchOnly,
  findLeadsMetadata,
  onLeadSaved,
}: Props) {
  const { createLeadFromDefaultValues, isLoading: isCreatingLead } = useCreateLeadOneClick({
    trackingData,
    defaultValues,
    metadata: findLeadsMetadata,
    onCreateLead: (leadId) => onLeadSaved?.(leadId),
  });

  const entitiesToAttach = resolveEntitiesToAttach(entities, entityType, entityIds);
  const { mutate: attachEntitiesAfterCreate } = useAddLeadEntities(trackingData, {
    onSuccess: (_data, variables) => onLeadSaved?.(variables.id),
    onError: () =>
      toast.error("Lead created, but the contact couldn't be added. Try saving it to the lead."),
  });

  const buyerIds = defaultValues?.entities?.filter(isBuyerOrganisation)?.flatMap((e) => e.entityId);

  const [filters, setFilters] = useState<SearchLeadsRequest>({
    orderBy: LeadSortBy.BuyerMatching,
    order: LeadSortOrder.Desc,
    orderBuyerMatchingIds: buyerIds,
    ...(buyerMatchOnly && { buyerFilters: { buyers: buyerIds } }),
  });

  const [debouncedFilters] = useDebouncedValue(filters, 500);
  const { data, hasNextPage, fetchNextPage, isLoading, isError } = useInfiniteLeads(
    debouncedFilters,
    MAX_MATCHING_BUYERS,
  );

  // Enriching leads data with what leads have been saved to already
  const allLeads = useMemo(() => {
    const leads =
      data?.pages
        .flatMap((page) => page.leads.results)
        .map((lead) => ({
          ...lead,
          isSaved: savedLeadIds.includes(lead.id),
        })) ?? [];

    return {
      leads,
      total: data?.pages[0]?.leads.total ?? 0,
    };
  }, [data?.pages, savedLeadIds]);

  // Show search bar if there are initial leads or if user is searching
  const hasInitialLeads =
    !filters.searchText && (data?.pages?.[0]?.leads?.results ?? []).length > 0;
  const showSearchBar = hasInitialLeads || !!filters.searchText;

  if (isCreateFormOpen) {
    return (
      <Container>
        <Text h2>Create new lead</Text>
        <CreateLeadForm
          trackingData={trackingData}
          defaultValues={defaultValues}
          findLeadsMetadata={findLeadsMetadata}
          onComplete={(leadId) => {
            onToggleCreateForm(false);
            if (!leadId) return;

            if (entitiesToAttach) {
              attachEntitiesAfterCreate({ id: leadId, entities: { entities: entitiesToAttach } });
            } else {
              onLeadSaved?.(leadId);
            }
          }}
        />
      </Container>
    );
  }

  return (
    <Container>
      {showSearchBar && (
        <Input
          placeholder="Search"
          prefix={<UIcon icon="searchRefraction" size={16} />}
          value={filters.searchText ?? ""}
          onChange={(e) => {
            setFilters({ ...filters, searchText: e.target.value });
          }}
          allowClear
        />
      )}
      <ListContainer id={LEAD_LIST_CONTAINER_ID} aria-label={LEAD_LIST_CONTAINER_ID}>
        <SaveToLeadList
          isLoading={isLoading}
          isError={isError}
          leads={allLeads.leads}
          total={allLeads.total}
          buyerIds={buyerIds}
          entityIds={entityIds}
          entityType={entityType}
          entities={entities}
          hasNextPage={hasNextPage || false}
          fetchNextPage={fetchNextPage}
          buyerMatchOnly={buyerMatchOnly}
          onLeadSaved={onLeadSaved}
        />
      </ListContainer>
      <Button
        buttonType="secondary"
        icon="plus"
        loading={isLoading || isCreatingLead}
        onClick={async (e) => {
          // If the entityType is notice, we need to create a new lead from the default values
          // Bypassing the form entirely
          if (entityType === "notice") {
            createLeadFromDefaultValues(e);
          } else {
            onToggleCreateForm(true);
          }
        }}
      >
        Create a new lead
      </Button>
    </Container>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  height: "100%",
});

const ListContainer = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  gap: 8,
  height: "100%",
  overflowY: "auto",
});
